import React from 'react'
import {
  Donation,
  DonationProps,
  DonationBlockOnInitialization,
  MAX_DONATION_AMOUNT,
  MIN_DONATION_AMOUNT,
} from '@classy/campaign-page-blocks'
import { useRouter } from 'next/router'
import { trackDonationFormInitEvent } from 'services/analytics/events/donationFormInitialization'
import { useFetchProgramDesignations } from './useFetchProgramDesignations'
import { useIntelligentAskOverrides } from './useIntelligentAskOverrides'
import { usePassportCurrencyConversion } from './usePassportCurrencyConversion'

interface DonationControllerProps extends DonationProps {
  sectionId?: string
}

/**
 * Donation block properties for tracking purposes.
 * See trackDonationFormInitEvent()
 */
export type DonationInitBlockState = DonationBlockOnInitialization

/**
 * Additional IAA related properties for tracking purposes.
 * See trackDonationFormInitEvent()
 */
export interface DonationInitIntelligentAskState {
  enabled: boolean
  onetimeEnabled: boolean
  recurringEnabled: boolean
  donationOtherAmount: number | null
  recurringOtherAmount: number | null
}

/**
 * Client-side wrapper for the Donation block.
 *
 * Note, the `block*` props refer to the block values compiled in getServersideProps.
 * The block values may have been overridden by URL query parameters (see donation.ts).
 */
export const DonationController = ({
  donationAmounts: blockDonationAmounts,
  recurringAmounts: blockRecurringAmounts,
  donationFrequencies: blockDonationFrequencies,
  currencyCode = 'USD',
  minDonationAmount: blockMinDonationAmount = MIN_DONATION_AMOUNT,
  maxDonationAmount: blockMaxDonationAmount = MAX_DONATION_AMOUNT,
  sectionId,
  campaignId,
  initialProgramDesignations,
  ...rest
}: DonationControllerProps) => {
  const router = useRouter()
  const { query } = router
  let urlAmountUsed = query.amount as string

  if (
    !blockDonationAmounts?.length ||
    !blockRecurringAmounts?.length ||
    !blockDonationFrequencies?.length
  ) {
    throw new Error(
      'DonationController expects donationAmounts, recurringAmounts, and donationFrequencies to be defined.',
    )
  }

  // Intelligent Ask Amount
  const {
    donationAmounts: iaaAdjustedDonationAmounts,
    recurringAmounts: iaaAdjustedRecurringAmounts,
    donationFrequencies,
    showRecurringNudgeTag,
    isLoading: iaaIsLoading,
    intelligentAskEnabled,
    intelligentAskAmountData,
  } = useIntelligentAskOverrides(
    blockDonationAmounts || [],
    blockRecurringAmounts || [],
    blockDonationFrequencies || [],
  )

  // Currency Conversion
  const {
    donationAmounts,
    recurringAmounts,
    minDonationAmount,
    maxDonationAmount,
    currencyCode: convertedCurrencyCode,
    isLoading: currencyIsLoading,
  } = usePassportCurrencyConversion(
    iaaAdjustedDonationAmounts,
    iaaAdjustedRecurringAmounts,
    blockMinDonationAmount,
    blockMaxDonationAmount,
    currencyCode,
  )

  const onInitialization = (initBlockstate: DonationBlockOnInitialization) => {
    const intelligentAskState = {
      enabled: intelligentAskEnabled,
      onetimeEnabled: Boolean(intelligentAskAmountData.onetimeEnabled),
      recurringEnabled: Boolean(intelligentAskAmountData.recurringEnabled),
      donationOtherAmount: intelligentAskAmountData.suggested_donation_amount,
      recurringOtherAmount: intelligentAskAmountData.suggested_recurring_amount,
    }

    /**
     * When IAA is enabled, the amount query param is ignored.
     * Don't send it as part of the analytics event.
     */
    if (intelligentAskEnabled) {
      urlAmountUsed = ''
    }

    trackDonationFormInitEvent({
      initBlockstate,
      urlAmountUsed,
      sectionId,
      intelligentAskState,
    })
  }

  const { fetchProgramDesignations } = useFetchProgramDesignations(
    campaignId,
    initialProgramDesignations,
  )

  return (
    <Donation
      {...rest}
      isLoading={iaaIsLoading || currencyIsLoading}
      campaignId={campaignId}
      currencyCode={convertedCurrencyCode}
      donationAmounts={donationAmounts}
      recurringAmounts={recurringAmounts}
      donationFrequencies={donationFrequencies}
      showRecurringNudgeTag={showRecurringNudgeTag}
      minDonationAmount={minDonationAmount}
      maxDonationAmount={maxDonationAmount}
      onInitialization={onInitialization}
      initialProgramDesignations={initialProgramDesignations}
      fetchProgramDesignations={fetchProgramDesignations}
    />
  )
}
